import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./CommercialReconciliation.css";
import { ProfileOverviewState } from "../../redux/reducers/getProfile/getProfileOverviewReducer";
import { useDispatch, useSelector } from "react-redux";
import { selectCommercialReconciliation, selectELSLookups, selectProfileGetOverview } from "../../redux/selectors/exportSelectorPaths";
import { ControlledAccordion, useAccordionProvider } from "@szhsin/react-accordion";
import AccordionItem, { DefaultAccordionProviderOptions } from "../common/AccordionItem/AccordionItem";
import ToolTip from "../common/ToolTip/ToolTip";
import Button from "../common/Button/Button";
import { getCommercialReconciliation, resetCommercialReconciliation } from "../../redux/actions/commercialReconciliation/commercialReconciliation";
import { BaseAsyncReducerState } from "../../redux/types";
import { GetElsLookupsState } from "../../redux/reducers/getLookups/getElsLookupsReducer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faScaleBalanced, faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import DataTable from "../common/DataTable/DataTable";
import { commaSeparate } from '../../utils/formatter';

export default function CommercialReconciliation(): JSX.Element{

    const dispatch = useDispatch()
    const { t } = useTranslation('lease_table');
    const elsLookupsState: GetElsLookupsState = useSelector(selectELSLookups)
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview)
    const commercialReconciliationState: BaseAsyncReducerState = useSelector(selectCommercialReconciliation)
    const profileTypeCode = profileOverview.profile.profile_type_code.toUpperCase()
    const campaignStatusCode = sessionStorage.getItem("campaignStatusCode")
    const currentSnapshotId = sessionStorage.getItem('currentSnapshotId') as string

    const [showReconciliationNoDifferenceRows, setShowReconciliationNoDifferenceRows] = useState(true)
    const [initialDataFetched, setInitialDataFetched] = useState(false);
    DefaultAccordionProviderOptions.onStateChange = ({ current }) => {
        if (current.isResolved && current.isEnter && !initialDataFetched) {
            setInitialDataFetched(true);
            getCommercialReconciliationData();
        }
    }
    const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)

    const commercialReconciliationHeading = [
        <th className="reconciliation-table-table-heading sequence-column" key="column-1-header">{t("internal-headings.sequence-number")}</th>,
        <th className="reconciliation-table-table-heading text-column" key="column-2-header">{t("internal-headings.tenancy-type")}</th>,
        <th className="reconciliation-table-table-heading text-column" key="column-3-header">{t("internal-headings.design-type")}</th>,
        <th className="reconciliation-table-table-heading" key="column-4-header">{t("internal-headings.airp-count")}</th>,
        <th className="reconciliation-table-table-heading" key="column-5-header">{t("internal-headings.airp-gla")}</th>,
        <th className="reconciliation-table-table-heading" key="column-6-header">{t("internal-headings.difference")}</th>,
        <th className="reconciliation-table-table-heading" key="column-7-header">{t("internal-headings.insight-count")}</th>,
        <th className="reconciliation-table-table-heading" key="column-8-header">{t("internal-headings.insight-gla")}</th>
    ]

    useEffect(() => {
        dispatch(resetCommercialReconciliation());
        // eslint-disable-next-line
      }, [])

    const getCommercialReconciliationData = () => {
        dispatch(getCommercialReconciliation(profileOverview.profile.profile_id, currentSnapshotId));
    }

    const buildReconciliationTableContent = () => {
    const tableBody: JSX.Element[] = [];
    let totalAirpCount = 0;
    let totalAirpGla = 0;
    let totalDifference = 0;
    let totalInsightCount = 0;
    let totalInsightGla = 0;
    if (commercialReconciliationState.success) {
        Object.entries(commercialReconciliationState.data as Record<string, Record<string, number>>).forEach(([key, value], index) => {
        const keyArr = key.split("-");
        totalAirpCount += value["airp-count"]
        totalAirpGla += value["airp-gla"]
        totalDifference += value["difference"]
        totalInsightCount += value["insight-count"]
        totalInsightGla += value["insight-gla"]
        if (value["difference"] !== 0 || showReconciliationNoDifferenceRows) {
            const tenancyTypeLookup = (elsLookupsState.dropdowns["tenancyType"] as Record<string, unknown>[]).find(tt => tt.tenancyTypeCode === keyArr[1])
            const designTypeLookup = (elsLookupsState.dropdowns["designType"] as Record<string, unknown>[]).find(dt => dt.unitDesignTypeCode === keyArr[2])
            tableBody.push (<tr key={`reconciliation-table-row-${index}`}>
            <td className="sequence-column"><div className={"airp-badge" + (keyArr[0] === "None" ? "" : " badge-suburb-brown")}>{keyArr[0] === "None" ? "" : keyArr[0]}</div></td>
            <td className="text-column">{tenancyTypeLookup ? tenancyTypeLookup["tenancyTypeDescription"] as string : ""}</td>
            <td className="text-column">{designTypeLookup ? designTypeLookup["unitDesignTypeDescription"] as string : ""}</td>
            <td>{commaSeparate(value["airp-count"])}</td>
            <td><div className="airp-badge badge-light-blue">{commaSeparate(value["airp-gla"])}</div></td>
            <td><div className={"airp-badge " + (value["difference"] > 0 ? "badge-lighter-blue": (value["difference"] < 0 ? "badge-lighter-suburb-yellow" : "badge-green"))}>{commaSeparate(Math.abs(value["difference"]))}</div></td>
            <td>{commaSeparate(value["insight-count"])}</td>
            <td><div className="airp-badge badge-suburb-yellow">{commaSeparate(value["insight-gla"])}</div></td>
            </tr>)
        }
        })
    }
    tableBody.push(<tr key={`reconciliation-table-row-count`} className="total-row">
        <td colSpan={3} className="total-cell">{t("internal-headings.total")}</td>
        <td>{commaSeparate(totalAirpCount)}</td>
        <td><div className="airp-badge badge-light-blue">{commaSeparate(totalAirpGla)}</div></td>
        <td><div className={"airp-badge " + (totalDifference > 0 ? "badge-lighter-blue": (totalDifference < 0 ? "badge-lighter-suburb-yellow" : "badge-green"))}>{commaSeparate(Math.abs(totalDifference))}</div></td>
        <td>{commaSeparate(totalInsightCount)}</td>
        <td><div className="airp-badge badge-suburb-yellow">{commaSeparate(totalInsightGla)}</div></td>
    </tr>)
    return (<>{tableBody}</>);
    }
    

    return (
        <>{(campaignStatusCode === "INTERNAL_IN_PROGRESS" && profileTypeCode === "BIP") &&
            <div className='internal-view-accordion reconciliation-table'> 
                <div className="accordion">
                    <ControlledAccordion providerValue={providerValue}>
                        <AccordionItem header={t("internal-headings.reconciliation-table")} initialEntered={false}>
                        <div className="accordion-controls reconciliation-table-controls">
                            <ToolTip contents={
                            <Button onClick={() => setShowReconciliationNoDifferenceRows(!showReconciliationNoDifferenceRows)} 
                            text={<FontAwesomeIcon icon={faScaleBalanced} />}
                            ariaLabel="hide no difference rows"/>
                            }>
                            {t('tooltips.reconciliation-toggle-no-difference')}
                            </ToolTip>
                            <ToolTip contents={
                            <Button onClick={() => getCommercialReconciliationData()}
                            text={<FontAwesomeIcon icon={faArrowsRotate} />}
                            ariaLabel="get the latest data" />
                            }>
                            {t('tooltips.reconciliation-fetch-data')}
                            </ToolTip>
                        </div>
                        <DataTable headings={commercialReconciliationHeading}
                            className="commercial-reconciliation-table internal-table">
                            {buildReconciliationTableContent()}
                        </DataTable>
                        </AccordionItem>
                    </ControlledAccordion>
                </div>
            </div>
        }</>
    )
}